/**
 * App — the route tree + chrome. Routes:
 *   /            Gallery (public)
 *   /g/:slug     Game page (public)
 *   /u/:handle   Public user page (public)
 *   /about       About us — the founders (public)
 *   /stats       Public platform stats (public)
 *   /deej        Deej Gallery — the mascot art collection (public)
 *   /deej/:cardId  Deej Gallery with that card's detail open (shareable deep link)
 *   /leaderboard Community Rankings (public)
 *   /login       Auth (public)
 *   /welcome     One-time profile claim (signed-in)
 *   /account     Profile settings (signed-in)
 *   /studio      Studio (signed-in account)
 *   /admin       Admin (role=superadmin)
 * Guarded routes use {@link RouteGuard}; the header shows role-aware nav.
 * The tree sits inside {@link MyBadgesProvider} (the signed-in user's ONE
 * `user_badges` subscription) so the App-level {@link BadgeCelebration}
 * overlay and the user's own badge wall share a single realtime channel.
 * Every route change counts one page view for this SPA session (the
 * trackVisit callable behind the public stats page) — fire-and-forget,
 * tracking must never break navigation. A signed-in user also beats presence
 * from here (usePresenceHeartbeat), which is what puts them on the home page's
 * Online rail.
 * The {@link ChatDock} (the Clubhouse) mounts here too, INSIDE the router but
 * outside the routes, so it floats over every page and survives navigation with
 * its draft intact. Its provider wraps the tree so anything — the presence rail,
 * for one — can open it with a seeded draft.
 */
import { useEffect, useState } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { BadgeCelebration } from './components/BadgeCelebration.js';
import { Header } from './components/Header.js';
import { RouteGuard } from './components/RouteGuard.js';
import { Gallery } from './pages/Gallery.js';
import { Explore } from './pages/Explore.js';
import { RecordedMatch } from './pages/RecordedMatch.js';
import { GamePage } from './pages/GamePage.js';
import { Leaderboard } from './pages/Leaderboard.js';
import { NotFound } from './pages/NotFound.js';
import { About } from './pages/About.js';
import { DeejGallery } from './pages/DeejGallery.js';
import { Stats } from './pages/Stats.js';
import { Login } from './pages/Login.js';
import { Welcome } from './pages/Welcome.js';
import { Account } from './pages/Account.js';
import { UserPage } from './pages/UserPage.js';
import { Studio } from './pages/Studio.js';
import { StudioCreate } from './pages/StudioCreate.js';
import { StudioGameHub } from './pages/StudioGameHub.js';
import { TraceExplorer } from './pages/TraceExplorer.js';
import { Admin } from './pages/Admin.js';
import { AdminStats } from './pages/AdminStats.js';
import { APP_VERSION } from './platform/env.js';
import { useServices } from './services/context.js';
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat.js';
import { MyBadgesProvider } from './services/useUserBadges.js';
import { ChatDockProvider } from './chat/chatDockContext.js';
import { ChatDock } from './chat/ChatDock.js';

const SESSION_KEY = 'djgg.sid';
const MOBILE_HOME_QUERY = '(max-width: 679px)';

/** `/` only chooses a home: a phone lands in the swipe feed, desktop in the
 * classic Gallery, which lives at its own `/gallery` route so either audience
 * can still reach it deliberately (menu, back links). Reacting to the query
 * also handles rotation/resizing without baking a user-agent guess into
 * routing. */
export function HomeRoute() {
  const [mobile, setMobile] = useState(() => window.matchMedia?.(MOBILE_HOME_QUERY).matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.(MOBILE_HOME_QUERY);
    if (!query) return;
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return <Navigate to={mobile ? '/explore' : '/gallery'} replace />;
}

/** The SPA session id — minted once per browser session (sessionStorage). */
function sessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

/** Count one portal page view per route change for this session. */
function useVisitTracking(): void {
  const services = useServices();
  const { pathname } = useLocation();
  useEffect(() => {
    services.recordVisit(sessionId()).catch(() => {
      /* observability must never break the portal */
    });
  }, [services, pathname]);
}

export function App() {
  useVisitTracking();
  usePresenceHeartbeat();
  const { pathname } = useLocation();
  const isExplore = pathname === '/explore' || pathname === '/explore/';
  return (
    <MyBadgesProvider>
      <ChatDockProvider>
        <div className={`app${isExplore ? ' app--explore' : ''}`}>
          <Header />
          <main className="app__main">
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/g/:slug/matches/:matchId" element={<RecordedMatch />} />
              <Route path="/g/:slug" element={<GamePage />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/about" element={<About />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/deej" element={<DeejGallery />} />
              <Route path="/deej/:cardId" element={<DeejGallery />} />
              <Route path="/login" element={<Login />} />
              <Route path="/u/:handle" element={<UserPage />} />
              <Route
                path="/welcome"
                element={
                  <RouteGuard requirement="authed">
                    <Welcome />
                  </RouteGuard>
                }
              />
              <Route
                path="/account"
                element={
                  <RouteGuard requirement="authed">
                    <Account />
                  </RouteGuard>
                }
              />
              <Route
                path="/studio"
                element={
                  <RouteGuard requirement="authed">
                    <Studio />
                  </RouteGuard>
                }
              />
              <Route
                path="/studio/create"
                element={<RouteGuard requirement="authed"><StudioCreate /></RouteGuard>}
              />
              <Route
                path="/studio/create/:id"
                element={<RouteGuard requirement="authed"><StudioCreate /></RouteGuard>}
              />
              <Route
                path="/studio/g/:id"
                element={
                  <RouteGuard requirement="authed">
                    <StudioGameHub />
                  </RouteGuard>
                }
              />
              <Route
                path="/studio/g/:id/trace/:version"
                element={
                  <RouteGuard requirement="authed">
                    <TraceExplorer />
                  </RouteGuard>
                }
              />
              <Route
                path="/admin"
                element={
                  <RouteGuard requirement="superadmin">
                    <Admin />
                  </RouteGuard>
                }
              />
              <Route
                path="/admin/stats"
                element={
                  <RouteGuard requirement="adminStats">
                    <AdminStats />
                  </RouteGuard>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <footer className="footer">
            <div className="container">djgg creator platform · v{APP_VERSION}</div>
          </footer>
          <BadgeCelebration />
          {!isExplore && <ChatDock />}
        </div>
      </ChatDockProvider>
    </MyBadgesProvider>
  );
}
